import * as React from 'react';
import type { VideoStoryCard } from '@org/model';
import { getVideoEmbedInfo } from '../../../utils';
import { formatStoryLabel } from './labels';
import { StoryIcon } from './story-bits';
import type { StoryCardComponentProps, StoryVideoReport } from './story-types';
import { pickLocalized, storyLangAttr, StoryCardHeading } from './story-utils';

type PlayState = StoryVideoReport['state'];

/**
 * Video card. Uploaded files and direct video links play in a native
 * <video> (the shell follows its real time); YouTube / Vimeo / Loom links play
 * in their own embedded player. Nothing ever plays by itself.
 */
export function StoryVideoCard({ card, ctx }: StoryCardComponentProps<VideoStoryCard>) {
  const { lang, labels, interactive, paused, onVideoReport, woman } = ctx;
  const url = card.url.trim();
  const embed = React.useMemo(() => getVideoEmbedInfo(url), [url]);
  const kind: StoryVideoReport['kind'] = !url ? 'none' : embed ? 'embed' : 'native';
  const caption = pickLocalized(card.caption, lang);

  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [state, setState] = React.useState<PlayState>('idle');
  const pausedByShell = React.useRef(false);

  const reportRef = React.useRef(onVideoReport);
  reportRef.current = onVideoReport;
  const report = React.useCallback(
    (next: PlayState) => {
      const v = videoRef.current;
      const fraction = v && v.duration > 0 && Number.isFinite(v.duration) ? Math.min(1, v.currentTime / v.duration) : 0;
      reportRef.current?.({ kind, state: next, fraction: next === 'ended' ? 1 : fraction });
    },
    [kind],
  );

  // Tell the shell what kind of page this is (and reset when the video changes).
  React.useEffect(() => {
    setState('idle');
    report('idle');
  }, [report, url]);

  // The shell's pause (button or hold) pauses the video; resuming continues it.
  React.useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    if (paused && !v.paused && !v.ended) {
      pausedByShell.current = true;
      v.pause();
    } else if (!paused && pausedByShell.current) {
      pausedByShell.current = false;
      void v.play()?.catch(() => undefined);
    }
  }, [paused]);

  const change = (next: PlayState) => {
    setState(next);
    report(next);
  };

  const onBigPlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.ended) v.currentTime = 0;
    void v.play()?.catch(() => undefined);
  };

  let media: React.ReactNode;
  if (kind === 'none') {
    media = <div className="zs-vid-empty">{labels.videoEmpty}</div>;
  } else if (!interactive) {
    media = (
      <>
        <div className="zs-vid-still" style={card.posterUrl ? { backgroundImage: `url("${card.posterUrl}")` } : undefined} />
        <span className="zs-vid-play" aria-hidden="true">
          <StoryIcon.Play />
        </span>
      </>
    );
  } else if (embed) {
    media = (
      <div className="zs-vid-frame zs-int">
        <iframe
          src={embed.embedUrl}
          title={formatStoryLabel(labels.videoFrameTitle, { name: woman.name })}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
          allowFullScreen
        />
      </div>
    );
  } else {
    media = (
      <>
        <video
          ref={videoRef}
          className="zs-int"
          src={url}
          poster={card.posterUrl || undefined}
          controls
          playsInline
          preload="metadata"
          data-testid="story-video"
          onPlay={() => change('playing')}
          onPause={(e) => {
            if (!e.currentTarget.ended) change('paused');
          }}
          onEnded={() => change('ended')}
          onTimeUpdate={() => report(state === 'idle' ? 'playing' : state)}
        />
        {state !== 'playing' ? (
          <button
            type="button"
            className={`zs-vid-play zs-int${state === 'ended' ? ' zs-rp' : ''}`}
            onClick={onBigPlay}
            aria-label={state === 'ended' ? labels.videoReplay : labels.videoPlay}
          >
            {state === 'ended' ? <StoryIcon.Replay /> : <StoryIcon.Play />}
          </button>
        ) : null}
      </>
    );
  }

  return (
    <div className="zs-vid" data-testid="story-card-video">
      <div className="zs-pg-in">
        <StoryCardHeading card={card} lang={lang} />
      </div>
      <div className="zs-vid-box">{media}</div>
      {caption.text ? (
        <p className="zs-vid-cap" lang={storyLangAttr(caption.lang)}>
          {caption.text}
        </p>
      ) : null}
      {interactive && kind !== 'none' ? <div className="zs-vid-skip">{labels.videoSkip}</div> : null}
    </div>
  );
}
